import { commentsPluginKey, updateComments } from '@adornis/web-editor/plugins/comments.js';
import type { DocumentSession } from '@adornis/web-editor/client/DocumentSession.js';
import { commentPlainText, commentSchema } from '@adornis/web-editor/comments.js';
import type { WebsiteBuilderNodeJSON } from '@adornis/web-editor/types.js';
import { useEffect, useState } from '@adornis/functional-lit/stateful.js';
import type { LandingLocale } from '../../shared/content/landingContent.js';
import type { StaticPageAudience } from '../../shared/content/staticPageContent.js';
import type {
  LandingEditorComment,
  LandingEditorCommentAnchor,
  LandingEditorCommentPageKey,
  LandingEditorCommentStatus,
} from '../../shared/editor/landingEditorComments.js';
import { LandingEditorClient, LandingEditorRequestError } from '../editor/landingEditorClient.js';
import {
  clearEditorCommentOverlays,
  syncEditorCommentOverlays,
  type EditorCommentScreenPoint,
} from './editorCommentOverlay.js';

export type EditorCommentScope = 'all' | 'page';
export interface EditorCommentDraft extends EditorCommentScreenPoint {
  anchor: LandingEditorCommentAnchor;
}

interface EditorCommentsStateOptions {
  documentSession: DocumentSession;
  isAuthenticated: boolean;
  enabled: boolean;
  pageKey: LandingEditorCommentPageKey;
  audience: StaticPageAudience;
  locale: LandingLocale;
  elementIds: readonly string[];
  anchorIds: Readonly<Record<string, string>>;
  onActivateProfile: (profileId: string) => void;
  onAuthorizationError: (error: unknown) => boolean;
  openRightPanel: () => void;
}

export const useEditorCommentsState = (options: EditorCommentsStateOptions) => {
  const [, setCommentRevision] = useState(0, []);
  useEffect(() => { let revision = 0; return options.documentSession.subscribe(() => setCommentRevision(++revision)); }, [options.documentSession]);
  const comments = [...(commentsPluginKey.getState(options.documentSession.state)?.comments ?? [])] as LandingEditorComment[];
  const setComments = (next: LandingEditorComment[]) => options.documentSession.dispatch(updateComments(options.documentSession.state, { comments: next, enabled: options.enabled }));
  const [isLoading, setIsLoading] = useState(false, []);
  const [error, setError] = useState('', []);
  const [draft, setDraft] = useState<EditorCommentDraft | null>(null, []);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null, []);
  const [popoverPoint, setPopoverPoint] = useState<EditorCommentScreenPoint | null>(null, []);
  const [reanchorCommentId, setReanchorCommentId] = useState<string | null>(null, []);
  const [scope, setScope] = useState<EditorCommentScope>('all', []);
  const [statusFilter, setStatusFilter] = useState<LandingEditorCommentStatus>('open', []);
  const [authorFilter, setAuthorFilter] = useState('all', []);

  const handleFailure = (failure: unknown, fallback: string) => {
    if (options.onAuthorizationError(failure)) return;
    setError(failure instanceof Error ? failure.message : fallback);
  };
  const refresh = async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      setComments(await LandingEditorClient.loadComments());
      setError('');
    } catch (failure) {
      handleFailure(failure, 'Kommentare konnten nicht geladen werden.');
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!options.isAuthenticated) {
      setComments([]);
      return;
    }
    void refresh(true);
    const unsubscribe = LandingEditorClient.subscribeComments(() => void refresh(), () => void refresh());
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 15_000);
    const focus = () => void refresh();
    window.addEventListener('focus', focus);
    return () => {
      unsubscribe();
      window.clearInterval(interval);
      window.removeEventListener('focus', focus);
    };
  }, [options.isAuthenticated]);

  const replaceComment = (next: LandingEditorComment) =>
    setComments(comments.some(comment => comment.id === next.id)
      ? comments.map(comment => (comment.id === next.id ? next : comment))
      : [next, ...comments]);

  const updateComment = async (
    comment: LandingEditorComment,
    update: { text?: string; body?: WebsiteBuilderNodeJSON; status?: LandingEditorCommentStatus; anchor?: LandingEditorCommentAnchor },
  ) => {
    try {
      const next = await LandingEditorClient.updateComment(comment.id, {
        expectedRevision: comment.revision,
        ...update,
      });
      replaceComment(next);
      if (update.status === 'resolved') setPopoverPoint(null);
      setError('');
      return next;
    } catch (failure) {
      if (failure instanceof LandingEditorRequestError && failure.status === 409) void refresh();
      handleFailure(failure, 'Kommentar konnte nicht geändert werden.');
      return null;
    }
  };

  const beginAt = (anchor: LandingEditorCommentAnchor, point: EditorCommentScreenPoint) => {
    const reanchor = comments.find(comment => comment.id === reanchorCommentId);
    if (reanchor) {
      setReanchorCommentId(null);
      void updateComment(reanchor, { anchor });
      return;
    }
    setDraft({ anchor, ...point });
    setActiveCommentId(null);
    setPopoverPoint(null);
    options.openRightPanel();
  };

  const submitDraft = async (body: WebsiteBuilderNodeJSON) => {
    const text = commentPlainText(commentSchema.nodeFromJSON(body));
    if (!draft) return;
    try {
      const comment = await LandingEditorClient.createComment({ text, body, anchor: draft.anchor, createdLocale: options.locale });
      replaceComment(comment);
      setDraft(null);
      setActiveCommentId(comment.id);
      setError('');
    } catch (failure) {
      handleFailure(failure, 'Kommentar konnte nicht erstellt werden.');
      throw failure;
    }
  };

  const selectComment = (comment: LandingEditorComment, point: EditorCommentScreenPoint | null = null) => {
    setActiveCommentId(comment.id);
    setPopoverPoint(point);
    setDraft(null);
    options.openRightPanel();
  };

  const deleteComment = async (comment: LandingEditorComment) => {
    if (!window.confirm('Diesen Kommentar endgültig löschen?')) return;
    try {
      await LandingEditorClient.deleteComment(comment.id, comment.revision);
      setComments(comments.filter(entry => entry.id !== comment.id));
      if (activeCommentId === comment.id) setActiveCommentId(null);
      setPopoverPoint(null);
      setError('');
    } catch (failure) {
      if (failure instanceof LandingEditorRequestError && failure.status === 409) void refresh();
      handleFailure(failure, 'Kommentar konnte nicht gelöscht werden.');
    }
  };

  useEffect(() => {
    if (!options.enabled) {
      clearEditorCommentOverlays();
      setDraft(null);
      setPopoverPoint(null);
      setReanchorCommentId(null);
      return;
    }
    const frameRequest = window.requestAnimationFrame(() =>
      syncEditorCommentOverlays({
        comments: comments.filter(comment =>
          comment.anchor.pageKey === options.pageKey && comment.anchor.audience === options.audience),
        activeCommentId,
        pageKey: options.pageKey,
        audience: options.audience,
        elementIds: options.elementIds,
        anchorIds: options.anchorIds,
        onActivateProfile: options.onActivateProfile,
        onCreate: beginAt,
        onMove: (comment, anchor) => void updateComment(comment, { anchor }),
        onSelect: (comment, point) => selectComment(comment, point),
      }),
    );
    return () => window.cancelAnimationFrame(frameRequest);
  }, [
    options.enabled,
    options.pageKey,
    options.audience,
    options.locale,
    options.elementIds.join('|'),
    JSON.stringify(options.anchorIds),
    comments,
    activeCommentId,
    reanchorCommentId,
  ]);

  const activeComment = comments.find(comment => comment.id === activeCommentId) ?? null;
  return {
    activeComment,
    activeCommentId,
    authorFilter,
    comments,
    draft,
    error,
    isLoading,
    popoverPoint,
    reanchorCommentId,
    scope,
    statusFilter,
    cancelDraft: () => setDraft(null),
    closePopover: () => setPopoverPoint(null),
    deleteComment,
    refresh,
    selectComment,
    setReanchorCommentId,
    setAuthorFilter,
    setScope,
    setStatusFilter,
    submitDraft,
    updateComment,
  };
};

export type EditorCommentsState = ReturnType<typeof useEditorCommentsState>;
